import React from "react";
import {
  FinancialApplicationFormSectionSummary,
  FinancialApplicationFormSectionSummaryDataRowType,
} from "../FinancialApplicationFormSectionSummary/FinancialApplicationFormSectionSummary";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import "./OptInsSummary.scss";
import { SUBSTEP_PATHS } from "@/config/stepPaths";

export interface OptInsSummaryProps {
  financialApplicationId: string;
  forPersonalAccounts: boolean;
}

export const OptInsSummary: React.FC<OptInsSummaryProps> = ({ financialApplicationId, forPersonalAccounts = false }) => {
  const { formValue } = useFinancialApplicationForm(financialApplicationId);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);

  // Helper function to check if an opt-in is enabled
  const isOptInEnabled = (depositApp: any, optInKey: string): boolean => {
    switch (optInKey) {
      case "e-statements":
        return depositApp.eStatementsOptIn?.isEStatementsOptInEnabled ?? true; // Default to true for e-statements
      case "checks":
        return depositApp.checksOptIn?.isChecksOptInEnabled ?? false;
      case "wires":
        return depositApp.wiresOptIn?.isWiresOptInEnabled ?? false;
      case "ach":
        return depositApp.achOptIn?.isAchOptInEnabled ?? false;
      case "positive-pay":
        return depositApp.positivePayOptIn?.isPositivePayOptInEnabled ?? false;
      case "remote-deposit":
        return depositApp.remoteDepositScannerOptIn?.isRemoteDepositScannerOptInEnabled ?? false;
      default:
        return false;
    }
  };

  // Create opt-ins data for each active deposit application
  const optInsData =
    formValue?.depositApplications
      ?.map((depositApp) => {
        const product = products.find((p) => p.id === depositApp.financialProductId);
        return { ...depositApp,
          productName: product?.productName,
          isPersonalProduct: product?.isPersonalProduct,
          isBusinessProduct: product?.isBusinessProduct,
          isIoltaProduct: product?.isIoltaProduct,
          hasEStatementsOptIn: product?.hasEStatementsOptIn,
          hasChecksOptIn: product?.hasChecksOptIn,
          hasWiresOptIn: product?.hasWiresOptIn,
          hasAchOptIn: product?.hasAchOptIn,
          hasPositivePayOptIn: product?.hasPositivePayOptIn,
          hasRemoteDepositScannerOptIn: product?.hasRemoteDepositScannerOptIn,
        };
      })
      .filter((depositApp) => forPersonalAccounts ? depositApp.isPersonalProduct : depositApp.isBusinessProduct || depositApp.isIoltaProduct)
      .filter((depositApp) => depositApp.isActive)
      .flatMap((depositApp, index) => {
        const optInRows = [];

        // Add product name as a header
        optInRows.push({
          label: <b style={{ fontSize: "1rem", fontWeight: "700" }}>Account Services - {depositApp.productName}</b>,
          value: "", // Empty value for header row
          type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue as const,
        });

        // Add each available opt-in service
        if (depositApp.hasEStatementsOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>E-Statements</span>,
            value: isOptInEnabled(depositApp, "e-statements"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        if (depositApp.hasChecksOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>Checks</span>,
            value: isOptInEnabled(depositApp, "checks"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        if (depositApp.hasWiresOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>Wires</span>,
            value: isOptInEnabled(depositApp, "wires"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        if (depositApp.hasAchOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>ACH</span>,
            value: isOptInEnabled(depositApp, "ach"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        if (depositApp.hasPositivePayOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>Positive Pay</span>,
            value: isOptInEnabled(depositApp, "positive-pay"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        if (depositApp.hasRemoteDepositScannerOptIn) {
          optInRows.push({
            label: <span style={{ marginLeft: "2rem" }}>Remote Deposit Scanner</span>,
            value: isOptInEnabled(depositApp, "remote-deposit"),
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelToggleValue as const,
          });
        }

        // Add a spacer row between products (except for the last product)
        if (index < formValue.depositApplications.filter((da) => da.isActive).length - 1) {
          optInRows.push({
            label: "",
            value: "",
            type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue as const,
          });
        }

        return optInRows;
      }) || [];

  // If no active deposit applications, show a message
  if (optInsData.length === 0) {
    return (
      <div className="opt-ins-summary">
        <FinancialApplicationFormSectionSummary
          title="Account Features & Preferences"
          data={[
            {
              label: "No active deposit applications found",
              value: "Please select products first",
              type: FinancialApplicationFormSectionSummaryDataRowType.LabelValue,
            },
          ]}
          navigateTo={`/step/${SUBSTEP_PATHS.AdditionalInfo.Preferences}`}
        />
      </div>
    );
  }

  return (
    <div className="opt-ins-summary">
      <FinancialApplicationFormSectionSummary
        title="Account Features & Preferences"
        data={optInsData}
        navigateTo={`/step/${SUBSTEP_PATHS.AdditionalInfo.Preferences}`}
      />
    </div>
  );
};
